import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Check,
    AlertTriangle,
    Loader2,
    Lock,
    ShieldCheck,
    FileSignature,
    ArrowRight,
    Wallet,
    Ban,
    Wrench,
    GitBranch,
} from "lucide-react";
import { Layout } from "@/components/Layout";
import { OutcomeBadge } from "@/components/OutcomeBadge";
import { ScenarioTabs } from "@/components/ScenarioTabs";
import {
    getCAElection,
    validateCAElection,
    repairCAElection,
    signCAElection,
} from "@/lib/api";

const PATHS = [
    { id: "CA-ELEC-001", label: "Clean election" },
    { id: "CA-ELEC-002", label: "Exception - missing mandate" },
];

const CHECK_ICON = {
    Pass: { icon: Check, cls: "text-green-600" },
    "Repair required": { icon: AlertTriangle, cls: "text-amber-600" },
};

export default function CAWorkbench() {
    const { eventId } = useParams();
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [stage, setStage] = useState("idle"); // idle | validating | validated | repairing | repaired | signing | signed
    const [validation, setValidation] = useState(null);
    const [signResult, setSignResult] = useState(null);

    const load = () => {
        setError(null);
        getCAElection(eventId)
            .then(setData)
            .catch((e) => setError(e?.response?.status === 404 ? "unknown" : "load_failed"));
    };

    useEffect(() => {
        setData(null);
        setError(null);
        setStage("idle");
        setValidation(null);
        setSignResult(null);
        getCAElection(eventId)
            .then(setData)
            .catch((e) => setError(e?.response?.status === 404 ? "unknown" : "load_failed"));
    }, [eventId]);

    const runValidation = async () => {
        setStage("validating");
        const res = await validateCAElection(eventId);
        setTimeout(() => {
            setValidation(res);
            setStage("validated");
        }, 650);
    };

    const runRepair = async () => {
        setStage("repairing");
        const res = await repairCAElection(eventId);
        setTimeout(() => {
            setValidation(res);
            setData((d) => ({
                ...d,
                election: { ...d.election, mandate_reference: res.mandate_reference },
                role_wallets: d.role_wallets.map((w) => ({ ...w, blocked: false })),
            }));
            setStage("repaired");
        }, 650);
    };

    const runSign = async () => {
        const isRepaired = validation?.decision?.outcome === "RELEASE";
        setStage("signing");
        const res = await signCAElection(eventId, isRepaired);
        setSignResult(res);
        setStage("signed");
    };

    if (error)
        return (
            <Layout>
                <div data-testid="ca-scenario-error">
                    <p className="text-sm text-slate-600">
                        {error === "unknown"
                            ? "Unknown synthetic election."
                            : "Scenario could not be loaded. Please return to the workbench or retry."}
                    </p>
                    <div className="mt-4 flex items-center gap-3">
                        {error !== "unknown" && (
                            <button
                                onClick={load}
                                data-testid="ca-scenario-retry-btn"
                                className="rounded-sm bg-blue-950 px-4 py-2 text-sm font-medium text-white hover:bg-blue-900"
                            >
                                Retry
                            </button>
                        )}
                        <Link to="/" className="text-sm text-blue-950 underline">
                            Return to overview
                        </Link>
                    </div>
                </div>
            </Layout>
        );

    if (!data)
        return (
            <Layout>
                <div className="flex items-center gap-2 text-sm text-slate-400">
                    <Loader2 className="h-4 w-4 animate-spin" /> Loading election envelope…
                </div>
            </Layout>
        );

    const { event, election, role_wallets, protocol } = data;
    const decision = validation?.decision || data.decision;
    const checks = validation?.checks || data.checks;
    const adapters = validation?.adapters || data.adapters;
    const isException = data.decision.outcome === "REVIEW";
    const showValidated = ["validated", "repairing", "repaired", "signing", "signed"].includes(stage);
    const canSign = (stage === "validated" && !isException) || stage === "repaired";

    return (
        <Layout wide>
            <ScenarioTabs active="ca" />

            {/* Election path selector */}
            <div className="mb-6 flex flex-wrap items-center gap-2">
                <span className="mr-1 text-xs font-mono uppercase tracking-[0.14em] text-slate-400">
                    Election path
                </span>
                {PATHS.map((p) => (
                    <Link
                        key={p.id}
                        to={`/request/${p.id}`}
                        data-testid={`ca-path-${p.id}`}
                        className={`rounded-sm border px-3 py-1 text-xs transition-colors duration-150 ${
                            p.id === eventId
                                ? "border-blue-950 bg-blue-950 text-white"
                                : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                    >
                        {p.label}
                    </Link>
                ))}
            </div>

            {/* Scenario header */}
            <section className="mb-6" data-testid="ca-scenario-header">
                <p className="text-xs font-mono uppercase tracking-[0.18em] text-slate-400">
                    Scenario 2 · Lifecycle control
                </p>
                <h1 className="mt-2 text-2xl font-medium tracking-tight text-slate-900">
                    Corporate Action Election Envelope
                </h1>
                <p className="mt-1 text-sm text-slate-500">
                    Lifecycle control with parent event, option and holder election dependency
                </p>
                <p className="mt-4 max-w-3xl text-sm leading-relaxed text-slate-600">
                    A corporate-action election is not simply another instruction. It is
                    a child lifecycle action that must bind to a valid parent event and
                    permitted option before any downstream token, register or
                    notification adapter may act.
                </p>
            </section>

            {/* Parent-child dependency strip */}
            <section className="mb-6 rounded-sm border border-slate-200 bg-white p-6" data-testid="ca-dependency">
                <div className="flex items-center gap-2">
                    <GitBranch className="h-4 w-4 text-blue-950" strokeWidth={1.5} />
                    <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                        Parent-child dependency
                    </p>
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2 font-mono text-xs">
                    <span className="rounded-sm border border-slate-300 bg-slate-50 px-3 py-1.5 text-slate-800" data-testid="ca-dep-parent">
                        Parent event · {event.event_ref}
                    </span>
                    <span className="text-slate-400">→</span>
                    <span className="rounded-sm border border-slate-300 bg-slate-50 px-3 py-1.5 text-slate-800" data-testid="ca-dep-option">
                        Option · {election.selected_option_ref}
                    </span>
                    <span className="text-slate-400">→</span>
                    <span className="rounded-sm border border-blue-950 bg-blue-950 px-3 py-1.5 text-white" data-testid="ca-dep-election">
                        Election · {election.election_ref}
                    </span>
                </div>
                <p className="mt-3 max-w-3xl text-xs leading-relaxed text-slate-500">
                    The election is a child lifecycle action. It can proceed only if it
                    binds to a valid parent corporate-action event and one permitted
                    option.
                </p>
            </section>

            {/* Parent event + election request cards */}
            <div className="mb-6 grid gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200 lg:grid-cols-2">
                <div className="bg-white p-7" data-testid="ca-parent-event-card">
                    <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                        Parent event
                    </p>
                    <dl className="mt-4 divide-y divide-slate-100 border-y border-slate-100">
                        <Field label="Event reference" value={event.event_ref} mono />
                        <Field label="Instrument" value={event.instrument_reference} />
                        <Field label="Event type" value={event.event_type} />
                        <Field label="Election window" value={event.election_window} />
                        <Field label="Policy profile" value={event.policy_profile} mono />
                        <Field
                            label="Available options"
                            value={event.options.map((o) => `${o.ref} ${o.label}`).join(" / ")}
                        />
                        <Field label="Issuer / CA agent" value={event.issuer_agent} />
                    </dl>
                </div>
                <div className="bg-white p-7" data-testid="ca-election-card">
                    <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                        Election request
                    </p>
                    <dl className="mt-4 divide-y divide-slate-100 border-y border-slate-100">
                        <Field label="Election reference" value={election.election_ref} mono />
                        <Field label="Acting role" value={election.acting_role} />
                        <Field label="Selected option" value={election.selected_option_label} />
                        <Field label="Position reference" value={election.position_reference} />
                        <Field
                            label="Mandate reference"
                            value={election.mandate_reference}
                            warn={election.mandate_reference === "missing"}
                        />
                        <Field label="Election timestamp" value={election.election_timestamp} mono />
                    </dl>
                    <p className="mt-4 text-xs text-slate-400">
                        <Lock className="mr-1 inline h-3 w-3" />
                        {election.withheld_evidence_count} evidence reference(s) withheld
                        under custody. References only enter the shared control flow.
                    </p>
                </div>
            </div>

            {/* Institutional role wallets */}
            <section className="mb-6 rounded-sm border border-slate-200 bg-white p-6" data-testid="ca-role-wallets">
                <div className="flex items-center gap-2">
                    <Wallet className="h-4 w-4 text-blue-950" strokeWidth={1.5} />
                    <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                        Institutional role wallets · lifecycle handoff
                    </p>
                </div>
                <div className="mt-5 grid gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200 lg:grid-cols-5">
                    {role_wallets.map((w, i) => (
                        <div
                            key={w.role}
                            data-testid={`ca-wallet-${i}`}
                            className={`bg-white p-4 ${w.blocked ? "opacity-60" : ""}`}
                        >
                            <p className="font-mono text-[10px] uppercase tracking-wide text-slate-400">
                                {w.role}
                            </p>
                            <p className="mt-1 text-sm font-medium leading-tight text-slate-900">
                                {w.institution}
                            </p>
                            <dl className="mt-3 space-y-2 text-xs">
                                <WalletField label="Custody" value={w.custody} />
                                <WalletField label="Authority" value={w.authority} />
                                <WalletField label="Protocol role" value={w.protocol_role} />
                            </dl>
                            {w.blocked && (
                                <p className="mt-3 inline-flex items-center gap-1 text-[11px] text-amber-700">
                                    <Ban className="h-3 w-3" /> no authorised action
                                </p>
                            )}
                            <p className="mt-3 text-[11px] text-slate-400">↓ {w.step}</p>
                        </div>
                    ))}
                </div>
                <p className="mt-3 text-[11px] italic text-slate-400">
                    Synthetic role-wallet model. Protocol: {protocol.protocol} · evidence
                    exchange: {protocol.evidence_exchange}.
                </p>
            </section>

            <div className="grid gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200 lg:grid-cols-[1fr_1fr]">
                {/* LEFT - envelope checks */}
                <div className="bg-white p-7">
                    <div className="flex items-center justify-between">
                        <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                            Envelope checks
                        </p>
                        {stage === "idle" && (
                            <button
                                onClick={runValidation}
                                data-testid="ca-run-validation-btn"
                                className="inline-flex items-center gap-2 rounded-sm bg-blue-950 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-900"
                            >
                                <ShieldCheck className="h-4 w-4" /> Evaluate election envelope
                            </button>
                        )}
                        {(stage === "validating" || stage === "repairing") && (
                            <span className="inline-flex items-center gap-2 text-sm text-slate-400">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                {stage === "repairing" ? "Rerunning validation…" : "Evaluating…"}
                            </span>
                        )}
                    </div>

                    {stage === "idle" && (
                        <p className="mt-4 text-sm text-slate-500">
                            The Control Decision Envelope verifies that required governance
                            conditions are satisfied: the election binds to a valid parent
                            event and option, the acting role is authorised, and required
                            evidence references are present. The configured issuer policy,
                            role mandate and evidence-reference checks determine the outcome.
                        </p>
                    )}

                    {showValidated && (
                        <div className="mt-4 fade-up" data-testid="ca-checks-table">
                            <table className="w-full border-collapse text-sm">
                                <thead>
                                    <tr className="border-b border-slate-200">
                                        <th className="py-2 text-left font-mono text-xs uppercase tracking-wide text-slate-400">Check</th>
                                        <th className="py-2 text-right font-mono text-xs uppercase tracking-wide text-slate-400">Result</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {checks.map((c, i) => {
                                        const m = CHECK_ICON[c.result] || CHECK_ICON["Pass"];
                                        return (
                                            <motion.tr
                                                key={c.key}
                                                initial={{ opacity: 0, x: 6 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: 0.05 * i }}
                                                className="border-b border-slate-100 last:border-0"
                                                data-testid={`ca-check-${c.key}`}
                                            >
                                                <td className="py-2.5 text-slate-700">{c.name}</td>
                                                <td className="py-2.5 text-right">
                                                    <span className={`inline-flex items-center gap-1.5 text-xs ${m.cls}`}>
                                                        <m.icon className="h-4 w-4" strokeWidth={1.8} />
                                                        {c.result}
                                                    </span>
                                                </td>
                                            </motion.tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* RIGHT - control envelope output */}
                <div className="bg-white p-7">
                    <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                        Control envelope output
                    </p>

                    {!showValidated && (
                        <p className="mt-4 text-sm text-slate-500">
                            Run the envelope checks to produce a decision that a token,
                            register or notification adapter can consume. The adapter
                            receives the authorised control decision - not raw evidence.
                        </p>
                    )}

                    {showValidated && (
                        <div className="mt-4 fade-up" data-testid="ca-envelope-output">
                            <div className="rounded-sm border border-slate-200 bg-slate-50 p-4">
                                <div className="flex items-center gap-3">
                                    <OutcomeBadge outcome={decision.outcome} />
                                    <span className="text-sm font-medium text-slate-900">{decision.headline}</span>
                                </div>
                                {decision.narrative && (
                                    <p className="mt-2 text-xs leading-relaxed text-slate-600" data-testid="ca-decision-narrative">
                                        {decision.narrative}
                                    </p>
                                )}
                                <div className="mt-3 grid grid-cols-1 gap-1.5 text-xs sm:grid-cols-2">
                                    <KV label="Envelope status" value={decision.envelope_status} />
                                    <KV label="Reason category" value={decision.reason_category} />
                                    <KV label="Permitted adapter action" value={decision.permitted_adapter_action} />
                                    <KV label="Authority source" value={decision.authority_source} />
                                    <KV label="KATLAS role" value={decision.katlas_role} />
                                    {decision.repair_owner && (
                                        <KV label="Repair owner" value={decision.repair_owner} />
                                    )}
                                </div>
                            </div>

                            {/* Exception path - repair */}
                            {isException && stage === "validated" && (
                                <div className="mt-4 rounded-sm border border-amber-200 bg-amber-50 p-4" data-testid="ca-exception-panel">
                                    <p className="text-sm font-medium text-amber-800">
                                        Governed pause - repair required
                                    </p>
                                    <p className="mt-1 text-xs leading-relaxed text-amber-800">
                                        Downstream adapter authority is withheld. The Holder
                                        Custodian must supply the missing mandate reference before
                                        validation is rerun.
                                    </p>
                                    <button
                                        onClick={runRepair}
                                        data-testid="ca-submit-repair-btn"
                                        className="mt-3 inline-flex items-center gap-2 rounded-sm bg-blue-950 px-4 py-2 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-900"
                                    >
                                        <Wrench className="h-4 w-4" />
                                        Submit repair as Holder Custodian
                                    </button>
                                </div>
                            )}

                            {stage === "repaired" && (
                                <p className="mt-3 text-xs text-green-700" data-testid="ca-release-note">
                                    Repair submitted → validation rerun → RELEASE. The
                                    previously paused election now satisfies the configured
                                    governance conditions.
                                </p>
                            )}

                            {/* Adapter strip */}
                            <div className="mt-5" data-testid="ca-adapter-strip">
                                <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                                    Downstream adapters
                                </p>
                                <div className="mt-2 grid grid-cols-2 gap-px overflow-hidden rounded-sm border border-slate-200 bg-slate-200">
                                    {adapters.map((a, i) => (
                                        <div
                                            key={a.name}
                                            data-testid={`ca-adapter-${i}`}
                                            className={`bg-white p-3 ${a.blocked ? "opacity-60" : ""}`}
                                        >
                                            <p className="font-mono text-[10px] uppercase tracking-wide text-slate-400">{a.name}</p>
                                            <p className="mt-0.5 text-xs text-slate-700">{a.action}</p>
                                            {a.blocked && (
                                                <p className="mt-1 inline-flex items-center gap-1 text-[10px] text-amber-700">
                                                    <Ban className="h-3 w-3" /> no authority received
                                                </p>
                                            )}
                                        </div>
                                    ))}
                                </div>
                                <p className="mt-2 text-[11px] leading-relaxed text-slate-400">
                                    The token, register or notification adapter consumes the
                                    authorised control decision. It does not receive raw
                                    evidence. No real token, register, payment or settlement is
                                    implemented.
                                </p>
                            </div>

                            {/* Sign */}
                            {canSign && (
                                <button
                                    onClick={runSign}
                                    data-testid="ca-sign-btn"
                                    className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-sm bg-blue-950 px-4 py-2.5 text-sm font-medium text-white transition-colors duration-150 hover:bg-blue-900"
                                >
                                    <FileSignature className="h-4 w-4" />
                                    Sign governance receipt through KATLAS node
                                </button>
                            )}
                            {stage === "signing" && (
                                <div className="mt-5 inline-flex items-center gap-2 text-sm text-slate-400">
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Contacting live KATLAS node…
                                </div>
                            )}
                            {stage === "signed" && signResult && (
                                <CASignOutcome result={signResult} decision={decision} />
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Why this matters */}
            <section className="mt-6 rounded-sm border border-slate-200 bg-white p-6" data-testid="ca-why-matters">
                <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                    Why this matters
                </p>
                <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">
                    Tokenised assets do not remove asset-servicing obligations. Corporate
                    actions still require issuer policy, valid options, holder
                    eligibility, custodian authority, deadlines, evidence references and
                    downstream notifications. The envelope carries the minimum authorised
                    state needed for execution without centralising raw evidence or
                    appointing KATLAS as controller.
                </p>
            </section>

            {/* Standards-oriented lifecycle context */}
            <section className="mt-6 rounded-sm border border-slate-200 bg-white p-6" data-testid="ca-standards-panel">
                <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                    Standards-oriented lifecycle context
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                    {[
                        "Corporate action event",
                        "Parent event reference",
                        "Option reference",
                        "Election instruction",
                        "Party / role",
                        "Financial instrument",
                        "Status / reason",
                        "Authority profile",
                        "Evidence-reference state",
                        "Receipt / attestation reference",
                        "Notification event",
                    ].map((c) => (
                        <span
                            key={c}
                            className="rounded-sm border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700"
                        >
                            {c}
                        </span>
                    ))}
                </div>
                <p className="mt-4 max-w-3xl text-xs leading-relaxed text-slate-500">
                    This is a standards-oriented control-envelope demonstration. Final
                    ISO 20022 field-level mapping remains implementation-specific and
                    subject to the relevant message definitions, usage guidelines and
                    market-practice requirements.
                </p>
            </section>
        </Layout>
    );
}

function CASignOutcome({ result, decision }) {
    const proofNode = result.signed
        ? "active"
        : result.katlas_state === "unconfigured"
        ? "honestly unconfigured"
        : result.katlas_state;

    return (
        <div className="mt-5 fade-up">
            <div className="rounded-sm border border-slate-300 bg-white p-4" data-testid="ca-governance-proof">
                <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                    KATLAS governance proof
                </p>
                <div className="mt-3 grid grid-cols-1 gap-1.5 text-xs sm:grid-cols-2">
                    <KV label="Node" value={proofNode} />
                    <KV label="Control decision" value={decision.outcome} />
                    <KV label="Evidence" value="References only; source evidence retained" />
                    <KV
                        label="Receipt"
                        value={result.signed ? "Signed by KATLAS node" : "Not produced (no fabrication)"}
                    />
                    <KV
                        label="Verification"
                        value={result.signed ? result.public?.verification_status || "verified" : "unavailable"}
                    />
                </div>
                <p className="mt-3 text-[11px] leading-relaxed text-slate-500">
                    KATLAS provides the operational-governance substrate, receipt and
                    verification infrastructure. It does not centralise raw evidence or
                    take discretionary control of the election.
                </p>
            </div>

            {!result.signed ? (
                <div className="mt-4 rounded-sm border border-slate-300 bg-slate-50 p-4" data-testid="ca-sign-unconfigured">
                    <div className="flex items-center gap-2">
                        <Lock className="h-4 w-4 text-slate-500" />
                        <p className="text-sm font-medium text-slate-800">
                            {result.katlas_label || "KATLAS unavailable"}
                        </p>
                    </div>
                    <p className="mt-2 text-xs leading-relaxed text-slate-600">{result.message}</p>
                    <p className="mt-2 text-xs leading-relaxed text-slate-500">
                        The control-envelope pattern is visible. Live KATLAS attestation
                        activates only when the backend-only production credential is
                        provisioned. No KATLAS-signed receipt is produced, fabricated or
                        simulated in this state.
                    </p>
                </div>
            ) : (
                <div data-testid="ca-sign-success">
                    <div className="mt-4 rounded-sm border border-slate-200 p-4">
                        <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                            Synthetic adapter handoff
                        </p>
                        <ul className="mt-2 space-y-1">
                            {result.execution.lines.map((l) => (
                                <li
                                    key={l}
                                    className={`flex items-center gap-2 text-sm ${
                                        result.execution.invoked ? "text-green-700" : "text-amber-700"
                                    }`}
                                >
                                    <Check className="h-4 w-4" /> {l}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="mt-4 rounded-sm border border-slate-200 bg-slate-50 p-4">
                        <p className="font-mono text-xs uppercase tracking-[0.14em] text-slate-400">
                            KATLAS receipt - verified by node
                        </p>
                        <div className="mt-2 grid grid-cols-1 gap-1 text-xs">
                            <KV label="Receipt hash" value={result.public.receipt_hash} mono />
                            <KV label="Signer / attestation" value={result.public.signer_attestation_reference} mono />
                            <KV label="Node timestamp" value={result.public.node_timestamp} mono />
                            <KV label="Verification status" value={result.public.verification_status} />
                        </div>
                        <Link
                            to={`/r/${result.public.receipt_hash}`}
                            data-testid="ca-open-receipt-link"
                            className="mt-4 inline-flex items-center gap-2 rounded-sm border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-900 transition-colors duration-150 hover:bg-slate-50"
                        >
                            Open public-safe receipt
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                    </div>
                </div>
            )}
        </div>
    );
}

function WalletField({ label, value }) {
    return (
        <div>
            <dt className="font-mono text-[10px] uppercase tracking-wide text-slate-400">{label}</dt>
            <dd className="mt-0.5 leading-snug text-slate-700">{value}</dd>
        </div>
    );
}

function Field({ label, value, mono, warn }) {
    return (
        <div className="flex items-start justify-between gap-4 py-2.5">
            <dt className="text-xs font-mono uppercase tracking-wide text-slate-400">{label}</dt>
            <dd
                className={`text-right text-sm ${warn ? "font-medium text-amber-700" : "text-slate-800"} ${
                    mono ? "font-mono" : ""
                }`}
            >
                {value}
            </dd>
        </div>
    );
}

function KV({ label, value, mono }) {
    const text = value == null ? "-"
        : typeof value === "object" ? Object.values(value).filter(Boolean).join(" · ")
        : value;
    return (
        <div className="flex flex-col">
            <span className="font-mono text-[10px] uppercase tracking-wide text-slate-400">{label}</span>
            <span className={`text-slate-800 ${mono ? "font-mono break-all" : ""}`}>{text}</span>
        </div>
    );
}
